import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, KeyRound, ShieldAlert, Car, Info, User, Calendar, ClipboardList, Pencil } from "lucide-react";
import JobCard from "@/components/JobCard";

export default function ClientDetailPage() {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [c, j] = await Promise.all([
                    api.get(`/clients/${clientId}`),
                    api.get(`/clients/${clientId}/jobs`),
                ]);
                setClient(c.data);
                setJobs(j.data);
            } catch (e) {
                toast.error(formatApiError(e));
                navigate("/clients");
            } finally {
                setLoading(false);
            }
        })();
    }, [clientId, navigate]);

    const { upcoming, past, stats } = useMemo(() => {
        const now = new Date();
        const upcoming = [];
        const past = [];
        for (const j of jobs) {
            const d = new Date(j.scheduled_at);
            if (j.status === "completed" || j.status === "cancelled" || d < now) past.push(j);
            else upcoming.push(j);
        }
        upcoming.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        past.sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
        const stats = {
            total: jobs.length,
            completed: jobs.filter(j => j.status === "completed").length,
            hours: Math.round(jobs.reduce((s, j) => s + (j.duration_minutes || 0), 0) / 60),
        };
        return { upcoming, past, stats };
    }, [jobs]);

    if (loading || !client) {
        return <div className="text-sm text-[var(--text-muted)]">Loading client…</div>;
    }

    return (
        <div className="space-y-6">
            <button onClick={() => navigate("/clients")}
                    data-testid="back-to-clients-btn"
                    className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--pine)] transition-colors">
                <ArrowLeft size={14} /> All clients
            </button>

            {/* Header */}
            <div className="card-flat overflow-hidden">
                {client.color && <div className="h-2.5" style={{ background: client.color }} />}
                <div className="p-6 sm:p-8 flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                        <span className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">Client dossier</span>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="client-detail-name">
                            {client.name}
                        </h1>
                        {client.contact_name && (
                            <p className="text-[var(--text-secondary)] mt-1 text-sm">Point of contact: <b>{client.contact_name}</b></p>
                        )}
                    </div>
                    <Link to="/clients" className="btn-ghost inline-flex items-center gap-2 text-sm">
                        <Pencil size={14} /> Edit in Client book
                    </Link>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 sm:gap-5" data-testid="client-stats">
                <StatMini icon={<ClipboardList size={16} />} label="Total jobs" value={stats.total} />
                <StatMini icon={<Calendar size={16} />} label="Completed" value={stats.completed} />
                <StatMini icon={<Info size={16} />} label="Hours booked" value={stats.hours} />
            </div>

            {/* Dossier facts */}
            <section className="card-flat p-6">
                <h2 className="font-display text-lg font-semibold mb-4">Site details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {client.address && <Row icon={<MapPin size={14} />} label="Address">{client.address}</Row>}
                    {client.contact_phone && (
                        <Row icon={<Phone size={14} />} label="Phone">
                            <a href={`tel:${client.contact_phone}`} className="hover:underline">{client.contact_phone}</a>
                        </Row>
                    )}
                    {client.contact_email && (
                        <Row icon={<Mail size={14} />} label="Email">
                            <a href={`mailto:${client.contact_email}`} className="hover:underline">{client.contact_email}</a>
                        </Row>
                    )}
                    {client.contact_name && <Row icon={<User size={14} />} label="Contact">{client.contact_name}</Row>}
                </div>

                {(client.gate_code || client.alarm_code || client.key_location) && (
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {client.gate_code && <CodeTile icon={<KeyRound size={14} />} label="Gate code" value={client.gate_code} />}
                        {client.alarm_code && <CodeTile icon={<ShieldAlert size={14} />} label="Alarm PIN" value={client.alarm_code} />}
                        {client.key_location && <CodeTile icon={<KeyRound size={14} />} label="Keys" value={client.key_location} />}
                    </div>
                )}

                {(client.access_notes || client.parking_notes) && (
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        {client.access_notes && (
                            <div className="flex items-start gap-2">
                                <span className="text-[var(--pine)] mt-0.5"><Info size={14} /></span>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">Access</div>
                                    <p className="whitespace-pre-line">{client.access_notes}</p>
                                </div>
                            </div>
                        )}
                        {client.parking_notes && (
                            <div className="flex items-start gap-2">
                                <span className="text-[var(--pine)] mt-0.5"><Car size={14} /></span>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">Parking</div>
                                    <p className="whitespace-pre-line">{client.parking_notes}</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Job history */}
            <SectionBlock title="Upcoming & in progress" testId="upcoming-jobs" jobs={upcoming} client={client}
                          empty="No upcoming jobs for this client. Schedule one from the dashboard." />
            <SectionBlock title="Past jobs" testId="past-jobs" jobs={past} client={client}
                          empty="No past jobs yet." />
        </div>
    );
}

function SectionBlock({ title, jobs, empty, testId, client }) {
    return (
        <section data-testid={testId}>
            <div className="flex items-center gap-2 mb-3">
                <h2 className="font-display text-xl font-semibold tracking-tight">{title}</h2>
                <span className="text-xs text-[var(--text-muted)]">({jobs.length})</span>
            </div>
            {jobs.length === 0 ? (
                <div className="card-flat p-6 text-sm text-[var(--text-secondary)]">{empty}</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {jobs.map(j => <JobCard key={j.id} job={j} clientColor={client?.color} />)}
                </div>
            )}
        </section>
    );
}

function StatMini({ icon, label, value }) {
    return (
        <div className="card-flat p-4">
            <div className="flex items-center gap-2 text-[var(--text-muted)] text-[10px] uppercase tracking-[0.18em] font-semibold">
                {icon}{label}
            </div>
            <div className="font-display text-2xl font-bold mt-1">{value}</div>
        </div>
    );
}

function Row({ icon, label, children }) {
    return (
        <div className="flex items-start gap-2">
            <span className="text-[var(--pine)] mt-0.5">{icon}</span>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">{label}</div>
                <div className="break-words">{children}</div>
            </div>
        </div>
    );
}

function CodeTile({ icon, label, value }) {
    return (
        <div className="rounded-xl p-3 border border-[var(--border-soft)]" style={{ background: "var(--pine-light)" }}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[var(--pine)] font-semibold">
                {icon} {label}
            </div>
            <div className="font-mono text-base font-semibold mt-1 break-all">{value}</div>
        </div>
    );
}
